<script setup lang="ts">
import {
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    CircleDashed,
    File,
    Loader2,
    Upload,
    X,
} from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { finalize as finalizeLibraryUpload } from '@/actions/App/Http/Controllers/LibraryController';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/composables/useToast';

interface Props {
    uploadUrl: string;
}

type UploadState =
    | 'queued'
    | 'preparing'
    | 'uploading'
    | 'finalizing'
    | 'uploaded'
    | 'failed';

interface UploadItem {
    localId: string;
    file: globalThis.File;
    progress: number;
    state: UploadState;
    error: string | null;
    remoteFileId: string | null;
}

interface PrepareUploadResponse {
    file: {
        id: string;
        original_filename: string;
        size_bytes: number;
    };
    upload: {
        url: string;
        headers: Record<string, string>;
        method: string;
    };
}

const props = defineProps<Props>();
const emit = defineEmits<{
    completed: [];
}>();

const toast = useToast();

const isDragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const uploadItems = ref<UploadItem[]>([]);
const isListExpanded = ref(true);
const isUploading = ref(false);

const fileFingerprint = (file: globalThis.File) =>
    [file.name.trim().toLowerCase(), file.size, file.lastModified].join('::');

const totalFiles = computed(() => uploadItems.value.length);
const completedFiles = computed(
    () => uploadItems.value.filter((item) => item.state === 'uploaded').length,
);
const failedFiles = computed(
    () => uploadItems.value.filter((item) => item.state === 'failed').length,
);
const overallProgress = computed(() => {
    if (uploadItems.value.length === 0) {
        return 0;
    }

    const total = uploadItems.value.reduce(
        (sum, item) => sum + item.progress,
        0,
    );

    return Math.round(total / uploadItems.value.length);
});
const overallLabel = computed(() => {
    if (isUploading.value) {
        return `${completedFiles.value}/${totalFiles.value} fichiers terminés`;
    }

    if (completedFiles.value === totalFiles.value && totalFiles.value > 0) {
        return 'Tous les fichiers ont été importés';
    }

    if (failedFiles.value > 0) {
        return `${failedFiles.value} fichier(s) en erreur`;
    }

    return totalFiles.value > 0
        ? 'Import terminé, vous pouvez ajouter d’autres fichiers'
        : 'Ajoutez vos PDF pour lancer l’import automatiquement';
});

const csrfToken = () =>
    document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? '';

const handleFileSelect = (e: Event) => {
    const files = (e.target as HTMLInputElement).files;

    if (files && files.length > 0) {
        processFiles(Array.from(files));
    }
};

const handleDrop = (e: DragEvent) => {
    isDragging.value = false;
    const files = e.dataTransfer?.files;

    if (files && files.length > 0) {
        processFiles(Array.from(files));
    }
};

const processFiles = (files: globalThis.File[]) => {
    const pdfFiles = files.filter((file) => file.type === 'application/pdf');
    const rejected = files.length - pdfFiles.length;
    const existingFingerprints = new Set(
        uploadItems.value.map((item) => fileFingerprint(item.file)),
    );
    const uniquePdfFiles: globalThis.File[] = [];
    let duplicateCount = 0;

    for (const file of pdfFiles) {
        const fingerprint = fileFingerprint(file);

        if (existingFingerprints.has(fingerprint)) {
            duplicateCount++;

            continue;
        }

        existingFingerprints.add(fingerprint);
        uniquePdfFiles.push(file);
    }

    if (rejected > 0) {
        toast.error(
            `${rejected} fichier(s) ignoré(s) : seuls les PDF sont acceptés.`,
        );
    }

    if (duplicateCount > 0) {
        toast.error(
            `${duplicateCount} fichier(s) déjà présents dans la file ont été ignorés.`,
        );
    }

    if (uniquePdfFiles.length > 0) {
        uploadItems.value = [
            ...uploadItems.value,
            ...uniquePdfFiles.map((file) => ({
                localId: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
                file,
                progress: 0,
                state: 'queued' as UploadState,
                error: null,
                remoteFileId: null,
            })),
        ];

        isListExpanded.value = true;
        void upload();
    }
};

const removeFile = (localId: string) => {
    uploadItems.value = uploadItems.value.filter(
        (item) => item.localId !== localId,
    );
};

const resetState = () => {
    uploadItems.value = [];
    isUploading.value = false;

    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const statusLabel = (state: UploadState) => {
    if (state === 'queued') {
        return 'En attente';
    }

    if (state === 'preparing') {
        return 'Préparation';
    }

    if (state === 'uploading') {
        return 'Upload en cours';
    }

    if (state === 'finalizing') {
        return 'Finalisation';
    }

    if (state === 'uploaded') {
        return 'Importé';
    }

    return 'Échec';
};

const statusIcon = (state: UploadState) => {
    if (state === 'uploaded') {
        return CheckCircle2;
    }

    if (state === 'failed') {
        return AlertCircle;
    }

    if (
        state === 'uploading' ||
        state === 'preparing' ||
        state === 'finalizing'
    ) {
        return Loader2;
    }

    return CircleDashed;
};

const statusIconClass = (state: UploadState) => {
    if (state === 'uploaded') {
        return 'text-emerald-600';
    }

    if (state === 'failed') {
        return 'text-rose-600';
    }

    if (
        state === 'uploading' ||
        state === 'preparing' ||
        state === 'finalizing'
    ) {
        return 'animate-spin text-brand';
    }

    return 'text-muted-foreground';
};

const uploadToStorage = async (
    item: UploadItem,
    method: string,
    url: string,
    headers: Record<string, string>,
) => {
    await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open(method, url, true);

        Object.entries(headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
        });

        const hasContentTypeHeader = Object.keys(headers).some(
            (key) => key.toLowerCase() === 'content-type',
        );

        if (!hasContentTypeHeader && item.file.type) {
            xhr.setRequestHeader('Content-Type', item.file.type);
        }

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) {
                return;
            }

            item.progress = Math.max(
                1,
                Math.round((event.loaded / event.total) * 100),
            );
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                item.progress = 100;
                resolve();

                return;
            }

            reject(new Error('Le stockage a refusé le fichier.'));
        };

        xhr.onerror = () =>
            reject(
                new Error('Impossible d’envoyer le fichier vers le stockage.'),
            );
        xhr.send(item.file);
    });
};

const prepareUpload = async (
    item: UploadItem,
): Promise<PrepareUploadResponse> => {
    const response = await fetch(props.uploadUrl, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
            original_filename: item.file.name,
            size_bytes: item.file.size,
            mime_type: item.file.type,
        }),
    });

    const payload = await response.json();

    if (!response.ok) {
        const message =
            typeof payload.message === 'string'
                ? payload.message
                : Object.values(payload.errors ?? {})[0];

        throw new Error(
            String(message ?? 'Erreur lors de la préparation de l’upload.'),
        );
    }

    return payload as PrepareUploadResponse;
};

const finalizeUpload = async (remoteFileId: string) => {
    const response = await fetch(finalizeLibraryUpload.url(remoteFileId), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
    });

    const payload = await response.json();

    if (!response.ok) {
        throw new Error(
            String(
                payload.message ??
                    'Erreur lors de la finalisation de l’upload.',
            ),
        );
    }
};

const upload = async () => {
    if (uploadItems.value.length === 0 || isUploading.value) {
        return;
    }

    isUploading.value = true;

    let uploadedCount = 0;

    while (true) {
        const item = uploadItems.value.find(
            (candidate) => candidate.state === 'queued',
        );

        if (!item) {
            break;
        }

        item.error = null;
        item.progress = 0;
        item.state = 'preparing';

        try {
            const prepared = await prepareUpload(item);
            item.remoteFileId = prepared.file.id;

            item.state = 'uploading';
            await uploadToStorage(
                item,
                prepared.upload.method,
                prepared.upload.url,
                prepared.upload.headers,
            );

            item.state = 'finalizing';
            await finalizeUpload(prepared.file.id);

            item.state = 'uploaded';
            item.progress = 100;
            uploadedCount++;
        } catch (error) {
            item.state = 'failed';
            item.error =
                error instanceof Error
                    ? error.message
                    : 'Erreur lors de l’upload.';
            toast.error(`${item.file.name}: ${item.error}`);
        }
    }

    isUploading.value = false;

    if (uploadItems.value.some((item) => item.state === 'queued')) {
        void upload();
    }

    if (uploadedCount > 0) {
        toast.success(`${uploadedCount} fichier(s) importé(s) avec succès.`);
        emit('completed');
    }
};
</script>

<template>
    <div class="space-y-4">
        <div
            class="rounded-2xl border border-border bg-card p-5 shadow-sm transition-colors"
            :class="[
                isDragging
                    ? 'border-brand bg-brand/5'
                    : 'hover:border-brand/40 hover:bg-muted/30',
            ]"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
        >
            <input
                ref="fileInput"
                type="file"
                class="hidden"
                accept="application/pdf"
                multiple
                @change="handleFileSelect"
            />

            <div
                class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
            >
                <div class="flex items-start gap-4">
                    <div
                        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand/8 text-brand"
                    >
                        <Upload class="h-5 w-5" />
                    </div>
                    <div class="space-y-1">
                        <div class="flex flex-wrap items-center gap-2">
                            <h3 class="text-sm font-semibold text-foreground">
                                Ajouter des PDF
                            </h3>
                            <span
                                class="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                            >
                                Démarrage automatique
                            </span>
                        </div>
                        <p class="text-sm text-muted-foreground">
                            Sélectionnez ou glissez-déposez vos fichiers.
                            L’import démarre immédiatement et les doublons déjà
                            présents dans la file sont ignorés.
                        </p>
                        <p class="text-xs text-muted-foreground">
                            PDF uniquement, jusqu’à 50 Mo par fichier.
                        </p>
                    </div>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        class="rounded-lg"
                        @click="fileInput?.click()"
                    >
                        Sélectionner des fichiers
                    </Button>
                    <Button
                        v-if="!isUploading && uploadItems.length > 0"
                        variant="ghost"
                        size="sm"
                        class="rounded-lg"
                        @click="resetState"
                    >
                        Vider la liste
                    </Button>
                </div>
            </div>
        </div>

        <div class="rounded-2xl border border-border bg-card shadow-sm">
            <div
                class="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4"
            >
                <div>
                    <h3 class="text-sm font-semibold text-foreground">
                        File d'attente d'import
                    </h3>
                    <p class="text-sm text-muted-foreground">
                        {{ overallLabel }}
                    </p>
                </div>
                <div
                    class="flex items-center gap-3 text-xs text-muted-foreground"
                >
                    <span>{{ completedFiles }} terminé(s)</span>
                    <span v-if="failedFiles > 0"
                        >{{ failedFiles }} en erreur</span
                    >
                    <button
                        v-if="uploadItems.length > 0"
                        type="button"
                        class="inline-flex items-center gap-2 font-medium text-brand hover:text-brand/80"
                        @click="isListExpanded = !isListExpanded"
                    >
                        {{
                            isListExpanded
                                ? 'Masquer la liste'
                                : 'Afficher la liste'
                        }}
                        <component
                            :is="isListExpanded ? ChevronUp : ChevronDown"
                            class="h-4 w-4"
                        />
                    </button>
                </div>
            </div>

            <div class="space-y-4 px-5 py-4">
                <div
                    v-if="uploadItems.length === 0"
                    class="rounded-xl bg-muted/40 px-4 py-6 text-sm text-muted-foreground"
                >
                    Aucun fichier dans la file pour le moment.
                </div>

                <template v-else>
                    <div class="space-y-2 rounded-xl bg-muted/35 p-4">
                        <div
                            class="flex items-center justify-between text-xs text-muted-foreground"
                        >
                            <span
                                >{{ totalFiles }} fichier(s) dans la file</span
                            >
                            <span>{{ overallProgress }}%</span>
                        </div>
                        <Progress :model-value="overallProgress" class="h-2" />
                    </div>

                    <div v-if="isListExpanded" class="space-y-2">
                        <div
                            v-for="item in uploadItems"
                            :key="item.localId"
                            class="rounded-xl border border-border/70 bg-background px-4 py-3"
                        >
                            <div class="flex items-start gap-3">
                                <div class="pt-0.5">
                                    <component
                                        :is="statusIcon(item.state)"
                                        class="h-5 w-5"
                                        :class="statusIconClass(item.state)"
                                    />
                                </div>
                                <div class="min-w-0 flex-1 space-y-1.5">
                                    <div class="flex items-center gap-2">
                                        <File class="h-4 w-4 text-brand" />
                                        <p
                                            class="truncate text-sm font-medium text-foreground"
                                        >
                                            {{ item.file.name }}
                                        </p>
                                    </div>
                                    <div
                                        class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
                                    >
                                        <span
                                            >{{
                                                (
                                                    item.file.size /
                                                    1024 /
                                                    1024
                                                ).toFixed(2)
                                            }}
                                            Mo</span
                                        >
                                        <span>•</span>
                                        <span>{{
                                            statusLabel(item.state)
                                        }}</span>
                                    </div>
                                    <Progress
                                        v-if="
                                            item.state === 'uploading' ||
                                            item.state === 'finalizing' ||
                                            item.state === 'uploaded'
                                        "
                                        :model-value="item.progress"
                                        class="mt-2 h-1.5"
                                    />
                                    <p
                                        v-if="item.error"
                                        class="text-xs text-rose-600"
                                    >
                                        {{ item.error }}
                                    </p>
                                </div>
                                <Button
                                    v-if="
                                        !isUploading &&
                                        item.state !== 'uploaded'
                                    "
                                    variant="ghost"
                                    size="icon"
                                    class="shrink-0 text-muted-foreground"
                                    @click="removeFile(item.localId)"
                                >
                                    <X class="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
